/* eslint-disable sonarjs/cognitive-complexity */
import { GetStaticPaths, GetStaticPropsContext, InferGetStaticPropsType } from 'next';

import { CHANNEL } from '@boss/constants/corporate-jobs';
import { getContentPage, getSlugMap } from '@boss/contentful-client/corporate-jobs';
import { job, news } from '@boss/services';
import type { ContentPageFields, LOCALE_CODE, SectionFields } from '@boss/types/corporate-jobs';
import type { IIVacancy } from '@boss/types/corporate-jobs';
import { isArrayWithContent } from '@boss/utils';

// FIXME: This is a temporary solution to ignore the enforce-module-boundaries rule that prevents circular dependencies
// eslint-disable-next-line @nx/enforce-module-boundaries
import { defaultLocale } from '../../../config/locales.config';
import { SEO } from '../components';
import { PageTypes } from '../constants/page';
import Layout from '../layout/default';
import ComponentMapper from '../mappers/ComponentMapper';
import getServerSideTranslations from '../utils/getServerSideTranslations';
import { mapContentfulToNextLocale, mapNextToContentfulLocale } from '../utils/localeMapper';
import { getMappedNavigation } from '../utils/navigation';

export const getStaticPaths: GetStaticPaths = async () => {
  const slugMap = await getSlugMap({
    preview: false,
    channel: CHANNEL,
    contentType: 'contentPage',
  });

  const pages = slugMap
    .filter(({ slug }) => !['404'].includes(slug))
    .map(({ slug, locale }) => ({
      params: {
        slug,
      },
      locale: mapContentfulToNextLocale(locale),
    }));

  return {
    paths: pages,
    /* 
      We need to set fallback to blocking, so that new pages can be previewed. 
      The 404 handling will take place in the getStaticProps function.
    */
    fallback: 'blocking',
  };
};

export const getStaticProps = async ({
  preview,
  params,
  locale,
}: GetStaticPropsContext<{ slug: string; locale: string }>) => {
  const contentfulLocale = mapNextToContentfulLocale(locale);

  const slug = params?.slug;

  if (!slug) {
    return {
      notFound: true,
    };
  }

  const [pageData, navigation] = await Promise.all([
    getContentPage<ContentPageFields>({
      slug,
      locale: contentfulLocale,
      preview: !!preview,
      channel: CHANNEL,
      defaultLocale,
    }),
    getMappedNavigation(contentfulLocale, !!preview),
  ]);

  if (!pageData || !pageData?.publishForLocales?.includes(contentfulLocale as LOCALE_CODE)) {
    return {
      notFound: true,
    };
  }

  const checkSectionHasWidget = (section: SectionFields, type: string) => {
    return section.content.some(
      contentItem => contentItem.__typename === 'widget' && contentItem.widgetType && contentItem.widgetType === type,
    );
  };

  const checkPageHasWidget = (type: string) => {
    return pageData.content
      .filter(contentItem => contentItem.__typename === 'section')
      .some(section => checkSectionHasWidget(section, type));
  };

  const highlightedJobs: IIVacancy[] | null = checkPageHasWidget('highlighted-jobs')
    ? await Promise.all(
        (await job.getVacancies({ locale: locale ?? 'nl' }))
          .filter(job => job.inDeKijker === true)
          .map(vacancy => job.getVacancy(vacancy.vacatureId.toString(), locale ?? 'nl')),
      ).then(jobs => jobs.filter((job): job is IIVacancy => job !== null))
    : null;

  const mostRecentJobs: IIVacancy[] | null = checkPageHasWidget('highlighted-jobs')
    ? await Promise.all(
        (
          await job.getVacancies({ locale: locale ?? 'nl' })
        ).map(vacancy => job.getVacancy(vacancy.vacatureId.toString(), locale ?? 'nl')),
      ).then(detailedJobs =>
        detailedJobs
          .filter((job): job is IIVacancy => job !== null)
          .sort((a, b) => Date.parse(b.laatstGewijzigdDatum ?? '') - Date.parse(a.laatstGewijzigdDatum ?? ''))
          .slice(0, 2),
      )
    : null;

  const highlightedNewsArticles = checkPageHasWidget('highlighted-news')
    ? await news.getAll(contentfulLocale ?? 'nl-BE')
    : null;

  const sortedAndTrimmedHighlightedArticles = highlightedNewsArticles
    ? highlightedNewsArticles
        .sort(function (artikel1, artikel2) {
          return Date.parse(artikel2.publicationDate) - Date.parse(artikel1.publicationDate);
        })
        .slice(0, 3)
    : null;

  return {
    props: {
      pageType: PageTypes.CONTENT,
      slug,
      locale: locale as string,
      highlightedJobs,
      mostRecentJobs,
      highlightedNews: sortedAndTrimmedHighlightedArticles,
      navigation,
      preview: !!preview,
      pageData: pageData ?? {},
      ...(await getServerSideTranslations(locale as string, ['common', 'contact', 'jobs', 'news'])),
    },
  };
};

const Index = ({
  pageData,
  preview,
  slug,
  locale,
  navigation,
  highlightedJobs,
  mostRecentJobs,
  highlightedNews,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
  const { metaKeywords, metaDescription, ogImage, translatedSlugs, noIndex, noFollow, pageTitle, themeColor } =
    pageData;

  return (
    <SEO
      description={metaDescription}
      imageSrc={ogImage?.image?.file.url}
      localizedSlugs={translatedSlugs}
      metaKeywords={metaKeywords}
      navigation={navigation}
      noFollow={noFollow}
      noIndex={noIndex}
      title={pageTitle}
    >
      <Layout localizedSlugs={translatedSlugs} navigation={navigation} preview={preview}>
        {isArrayWithContent(pageData?.content) &&
          pageData.content.map(entry => (
            <ComponentMapper
              entry={entry}
              highlightedJobs={highlightedJobs}
              highlightedNews={highlightedNews}
              key={entry.id}
              locale={locale}
              mostRecentJobs={mostRecentJobs}
              themeColor={themeColor}
            />
          ))}
      </Layout>
    </SEO>
  );
};

export default Index;
